import React, { useState, useEffect, useContext } from 'react';
import { Card, Row, Col, Form, Button, Spinner, Alert } from 'react-bootstrap';
import guestGuideApi from '../../services/guestGuideApi';
import useLanguage from '../../hooks/useLanguage';
import { AuthContext } from '../../context/AuthContext';
import { SUPERVISOR_ROLES } from '../../config/roleConfig';
import { formatDateTime } from '../../utils/dateFormatter';

/**
 * «دليل الضيف العام» — content shared by every unit's guest guide and printed sheet.
 * tasks/guest-guide-sections-and-print-sheet.md (G2 / G12).
 *
 * Saved with an explicit button, not auto-save: this text lands on every unit at once, so a
 * half-typed rule must never go live mid-sentence.
 *
 * Only CHANGED fields are sent — the server applies partial updates (G1), so two supervisors
 * editing different fields cannot overwrite each other's work.
 */

const TEXT_FIELDS = [
  'quietHoursAr', 'quietHoursEn',
  'lostItemsAr', 'lostItemsEn',
  'wasteNoteAr', 'wasteNoteEn',
  'sustainabilityAr', 'sustainabilityEn',
  'customerServicePhone',
  'socialHandle'
];

// Rules are edited as one-per-line text and stored as a list.
const toLines = (list) => (list || []).join('\n');
const fromLines = (text) => text.split('\n').map((l) => l.trim()).filter(Boolean);

const toForm = (d) => ({
  ...Object.fromEntries(TEXT_FIELDS.map((f) => [f, d[f] || ''])),
  houseRulesAr: toLines(d.houseRulesAr),
  houseRulesEn: toLines(d.houseRulesEn),
  emergencyNumbers: (d.emergencyNumbers || []).map((n) => ({ ...n }))
});

const toPayload = (form) => ({
  ...Object.fromEntries(TEXT_FIELDS.map((f) => [f, form[f]])),
  houseRulesAr: fromLines(form.houseRulesAr),
  houseRulesEn: fromLines(form.houseRulesEn),
  emergencyNumbers: form.emergencyNumbers
});

const changedFields = (form, saved) => {
  const next = toPayload(form);
  const prev = toPayload(saved);
  return Object.fromEntries(
    Object.keys(next)
      .filter((k) => JSON.stringify(next[k]) !== JSON.stringify(prev[k]))
      .map((k) => [k, next[k]])
  );
};

const GeneralGuestGuideTab = () => {
  const { t, isArabic, language } = useLanguage();
  const { user } = useContext(AuthContext);
  const canEdit = SUPERVISOR_ROLES.includes(user?.role);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [justSaved, setJustSaved] = useState(false);
  const [meta, setMeta] = useState({ isSaved: false, updatedAt: null });
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);

  const apply = (defaults) => {
    const f = toForm(defaults);
    setSaved(f);
    setForm(f);
    setMeta({ isSaved: defaults.isSaved, updatedAt: defaults.updatedAt });
  };

  useEffect(() => {
    guestGuideApi.getDefaults()
      .then(apply)
      .catch((err) => {
        console.error('Error loading guest guide defaults:', err);
        setError(t('guestGuide.general.loadError'));
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (field, value) => {
    setJustSaved(false);
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const setNumber = (index, key, value) => {
    setJustSaved(false);
    setForm((prev) => ({
      ...prev,
      emergencyNumbers: prev.emergencyNumbers.map((n, i) => (i === index ? { ...n, [key]: value } : n))
    }));
  };

  const addNumber = () => setField('emergencyNumbers', [...form.emergencyNumbers, { labelAr: '', labelEn: '', number: '' }]);
  const removeNumber = (index) => setField('emergencyNumbers', form.emergencyNumbers.filter((_, i) => i !== index));

  // The first save sends everything, so the suggested emergency numbers are persisted with it.
  const pending = form && saved ? (meta.isSaved ? changedFields(form, saved) : toPayload(form)) : {};
  const hasChanges = Object.keys(pending).length > 0;
  // The server drops an emergency row missing its Arabic label or number; stop the save instead so
  // a half-filled row is never silently lost.
  const incompleteNumber = form?.emergencyNumbers.some((n) => !n.labelAr.trim() || !n.number.trim());

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      apply(await guestGuideApi.updateDefaults(pending));
      setJustSaved(true);
    } catch (err) {
      console.error('Error saving guest guide defaults:', err);
      setError(err.response?.data?.error || t('guestGuide.general.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!form) {
    return <Alert variant="danger">{error}</Alert>;
  }

  const align = { textAlign: isArabic ? 'right' : 'left' };
  const ltr = { direction: 'ltr', textAlign: 'left' };

  const pair = (labelKey, arField, enField, rows = 2, hintKey = null) => (
    <Card className="border-0 shadow-sm mb-4">
      <Card.Body>
        <h5 className="mb-1" style={{ color: '#471396' }}>{t(labelKey)}</h5>
        {hintKey && <p className="text-muted small mb-3">{t(hintKey)}</p>}
        <Row>
          <Col md={6} className="mb-2">
            <Form.Label className="small text-muted">{t('guestGuide.general.arabic')}</Form.Label>
            <Form.Control
              as="textarea" rows={rows} dir="rtl" style={{ textAlign: 'right' }}
              value={form[arField]} disabled={!canEdit}
              onChange={(e) => setField(arField, e.target.value)}
            />
          </Col>
          <Col md={6} className="mb-2">
            <Form.Label className="small text-muted">{t('guestGuide.general.english')}</Form.Label>
            <Form.Control
              as="textarea" rows={rows} dir="ltr" style={ltr}
              value={form[enField]} disabled={!canEdit}
              onChange={(e) => setField(enField, e.target.value)}
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  return (
    <div dir={isArabic ? 'rtl' : 'ltr'} style={align}>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>
      )}

      <p className="text-muted">{t('guestGuide.general.intro')}</p>
      {!canEdit && <Alert variant="secondary" className="py-2">{t('guestGuide.general.readOnly')}</Alert>}
      {!meta.isSaved && <Alert variant="warning" className="py-2">{t('guestGuide.general.notSavedYet')}</Alert>}

      {pair('guestGuide.general.houseRules', 'houseRulesAr', 'houseRulesEn', 5, 'guestGuide.general.houseRulesHint')}
      {pair('guestGuide.general.quietHours', 'quietHoursAr', 'quietHoursEn', 1)}
      {pair('guestGuide.general.lostItems', 'lostItemsAr', 'lostItemsEn')}
      {pair('guestGuide.general.wasteNote', 'wasteNoteAr', 'wasteNoteEn', 2, 'guestGuide.general.wasteNoteHint')}
      {pair('guestGuide.general.sustainability', 'sustainabilityAr', 'sustainabilityEn')}

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <h5 className="mb-3" style={{ color: '#471396' }}>{t('guestGuide.general.customerServicePhone')}</h5>
          <Form.Control
            type="tel" dir="ltr" style={{ ...ltr, maxWidth: '260px' }}
            value={form.customerServicePhone} disabled={!canEdit}
            onChange={(e) => setField('customerServicePhone', e.target.value)}
          />
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <h5 className="mb-1" style={{ color: '#471396' }}>{t('guestGuide.general.socialHandle')}</h5>
          <p className="text-muted small mb-3">{t('guestGuide.general.socialHandleHint')}</p>
          <Form.Control
            dir="ltr" style={{ ...ltr, maxWidth: '260px' }} placeholder="hostme_ksa"
            aria-label={t('guestGuide.general.socialHandle')}
            value={form.socialHandle} disabled={!canEdit}
            onChange={(e) => setField('socialHandle', e.target.value)}
          />
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <h5 className="mb-3" style={{ color: '#471396' }}>{t('guestGuide.general.emergencyNumbers')}</h5>
          {form.emergencyNumbers.map((n, i) => (
            // Index keys are safe here: rows are only appended or removed, never reordered.
            // eslint-disable-next-line react/no-array-index-key
            <Row key={i} className="g-2 mb-2 align-items-end">
              <Col xs={12} md={4}>
                {i === 0 && <Form.Label className="small text-muted">{t('guestGuide.general.labelAr')}</Form.Label>}
                <Form.Control dir="rtl" style={{ textAlign: 'right' }} value={n.labelAr} disabled={!canEdit}
                  onChange={(e) => setNumber(i, 'labelAr', e.target.value)} />
              </Col>
              <Col xs={12} md={4}>
                {i === 0 && <Form.Label className="small text-muted">{t('guestGuide.general.labelEn')}</Form.Label>}
                <Form.Control dir="ltr" style={ltr} value={n.labelEn} disabled={!canEdit}
                  onChange={(e) => setNumber(i, 'labelEn', e.target.value)} />
              </Col>
              <Col xs={8} md={2}>
                {i === 0 && <Form.Label className="small text-muted">{t('guestGuide.general.number')}</Form.Label>}
                <Form.Control type="tel" dir="ltr" style={ltr} value={n.number} disabled={!canEdit}
                  onChange={(e) => setNumber(i, 'number', e.target.value)} />
              </Col>
              {canEdit && (
                <Col xs={4} md={2}>
                  <Button variant="outline-danger" className="w-100" onClick={() => removeNumber(i)}>
                    {t('guestGuide.general.remove')}
                  </Button>
                </Col>
              )}
            </Row>
          ))}
          {canEdit && (
            <Button variant="outline-primary" size="sm" className="mt-2" onClick={addNumber}>
              <i className="bi bi-plus" /> {t('guestGuide.general.addNumber')}
            </Button>
          )}
        </Card.Body>
      </Card>

      {canEdit && (
        <div className="d-flex align-items-center gap-3 flex-wrap mb-4">
          <Button
            style={{ background: '#471396', borderColor: '#471396', minHeight: '44px', minWidth: '120px' }}
            disabled={saving || !hasChanges || incompleteNumber}
            onClick={handleSave}
          >
            {saving ? t('guestGuide.general.saving') : t('guestGuide.general.save')}
          </Button>
          {incompleteNumber && (
            <span className="text-danger small">{t('guestGuide.general.incompleteNumber')}</span>
          )}
          {justSaved && !hasChanges && (
            <span className="text-success"><i className="bi bi-check-circle" /> {t('guestGuide.general.saved')}</span>
          )}
          {meta.updatedAt && (
            <small className="text-muted">
              {t('guestGuide.general.lastSaved')}: {formatDateTime(meta.updatedAt, language)}
            </small>
          )}
        </div>
      )}
    </div>
  );
};

export default GeneralGuestGuideTab;
